import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import Modal from '../../../common/components/organisms/Modal';
import { getAnnotationColor, getAnnotationIcon } from '../utils/annotationPresentation';
import {
  getAnnotationAlarmStateLabel,
  getAnnotationDescriptionDisplay,
  getAnnotationSeverityLabel,
} from '../utils/annotationDescriptionDisplay';
import { getEventDefinitionLabel } from 'home/Activity/helpers/activityEventDisplay';
import {
  ModalContent,
  HeaderSection,
  IconCircle,
  HeaderText,
  Title,
  SeverityBadge,
  StateBadge,
  Subtitle,
  ContentSection,
  SectionTitle,
  Description,
  MetadataGrid,
  MetadataItem,
  MetadataLabel,
  MetadataValue,
  AlarmSpecificSection,
  AlarmField,
  AlarmLabel,
  AlarmValue,
} from './AlarmAnnotationModal.styled';

const AlarmAnnotationModal = ({ annotation, onClose }) => {
  const { t } = useTranslation();
  const typeColor = getAnnotationColor(annotation, { respectSeverity: true });
  const typeIcon = getAnnotationIcon(annotation.type);
  const baseMetadata = annotation.base_metadata || {};

  // Format alarm state
  const alarmState = baseMetadata.state || 'unknown';
  const stateDisplay = getAnnotationAlarmStateLabel(t, alarmState);

  const templateName = baseMetadata.template_name
    ? getEventDefinitionLabel(t, baseMetadata.template_name, baseMetadata.template_name.replace(/_/g, ' '))
    : t('common.na');
  const description = getAnnotationDescriptionDisplay(t, annotation);

  return (
    <Modal
      title={t('annotations.alarmModal.title')}
      close={onClose}
      footer={() => (
        <button onClick={onClose}>{t('common.close')}</button>
      )}
    >
      <ModalContent>
        <HeaderSection>
          <IconCircle bg={typeColor}>
            <span aria-hidden="true">{typeIcon}</span>
          </IconCircle>
          <HeaderText>
            <Title>
              {t('annotations.alarmModal.alarm')}
              {annotation.severity && (
                <SeverityBadge severity={annotation.severity}>
                  {getAnnotationSeverityLabel(t, annotation.severity)}
                </SeverityBadge>
              )}
              <StateBadge state={alarmState}>
                {stateDisplay}
              </StateBadge>
            </Title>
            <Subtitle>
              {t('annotations.alarmModal.createdAt', { timestamp: moment(annotation.created_at).format('MMM DD, YYYY HH:mm') })}
            </Subtitle>
          </HeaderText>
        </HeaderSection>

        <ContentSection>
          <SectionTitle>{t('annotations.modal.sections.description')}</SectionTitle>
          <Description>
            {description || t('annotations.modal.noDescription')}
          </Description>
        </ContentSection>

        <ContentSection>
          <SectionTitle>{t('annotations.alarmModal.sections.alarmDetails')}</SectionTitle>
          <MetadataGrid>
            <MetadataItem>
              <MetadataLabel>{t('annotations.modal.fields.type')}</MetadataLabel>
              <MetadataValue>{t('annotations.alarmModal.alarm')}</MetadataValue>
            </MetadataItem>
            <MetadataItem>
              <MetadataLabel>{t('annotations.modal.fields.severity')}</MetadataLabel>
              <MetadataValue>
                {annotation.severity
                  ? getAnnotationSeverityLabel(t, annotation.severity)
                  : t('common.na')}
              </MetadataValue>
            </MetadataItem>
            <MetadataItem>
              <MetadataLabel>{t('annotations.alarmModal.fields.state')}</MetadataLabel>
              <MetadataValue>{stateDisplay}</MetadataValue>
            </MetadataItem>
            <MetadataItem>
              <MetadataLabel>{t('annotations.modal.fields.startTime')}</MetadataLabel>
              <MetadataValue>
                {moment(annotation.start_time).format('MMM DD, YYYY HH:mm')}
              </MetadataValue>
            </MetadataItem>
            {annotation.end_time && (
              <MetadataItem>
                <MetadataLabel>{t('annotations.modal.fields.endTime')}</MetadataLabel>
                <MetadataValue>
                  {moment(annotation.end_time).format('MMM DD, YYYY HH:mm')}
                </MetadataValue>
              </MetadataItem>
            )}
            {!annotation.end_time && (
              <MetadataItem>
                <MetadataLabel>{t('annotations.alarmModal.fields.duration')}</MetadataLabel>
                <MetadataValue>{t('annotations.alarmModal.ongoing')}</MetadataValue>
              </MetadataItem>
            )}
          </MetadataGrid>
        </ContentSection>

        <AlarmSpecificSection>
          <SectionTitle>{t('annotations.alarmModal.sections.eventInformation')}</SectionTitle>

          {baseMetadata.template_name && (
            <AlarmField>
              <AlarmLabel>{t('annotations.alarmModal.fields.templateName')}</AlarmLabel>
              <AlarmValue>{templateName}</AlarmValue>
            </AlarmField>
          )}

          {baseMetadata.event_definition_id && (
            <AlarmField>
              <AlarmLabel>{t('annotations.alarmModal.fields.eventDefinitionId')}</AlarmLabel>
              <AlarmValue>{baseMetadata.event_definition_id}</AlarmValue>
            </AlarmField>
          )}

          {baseMetadata.machine_id && (
            <AlarmField>
              <AlarmLabel>{t('annotations.alarmModal.fields.machineId')}</AlarmLabel>
              <AlarmValue>{baseMetadata.machine_id}</AlarmValue>
            </AlarmField>
          )}

          {baseMetadata.tag_id && (
            <AlarmField>
              <AlarmLabel>{t('annotations.alarmModal.fields.tagId')}</AlarmLabel>
              <AlarmValue>{baseMetadata.tag_id}</AlarmValue>
            </AlarmField>
          )}

          <AlarmField>
            <AlarmLabel>{t('annotations.modal.fields.sourceId')}</AlarmLabel>
            <AlarmValue>{annotation.source_id || t('common.na')}</AlarmValue>
          </AlarmField>
        </AlarmSpecificSection>
      </ModalContent>
    </Modal>
  );
};

AlarmAnnotationModal.propTypes = {
  annotation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    start_time: PropTypes.string.isRequired,
    end_time: PropTypes.string,
    description: PropTypes.string,
    severity: PropTypes.oneOf(['low', 'medium', 'high', 'critical']),
    created_by: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    created_by_name: PropTypes.string,
    created_at: PropTypes.string,
    source_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    source_type: PropTypes.string,
    base_metadata: PropTypes.object
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

export default AlarmAnnotationModal;
